import { byId } from '../domUtils';
import type { Root } from '../prototypeTypes';

type LinkDraft = { url: string; label: string };
type AttachmentDraft = { objectKey: string; submissionId: string; name: string; type: string; size: string };

export type IdeaDraftSnapshot = {
	title: string;
	subtitle: string;
	strengths: string;
	weaknesses: string;
	links: LinkDraft[];
	attachments: AttachmentDraft[];
	step: number;
};

export type AiDraftSnapshot = IdeaDraftSnapshot & {
	chapters: string;
	preset: string;
};

export type FeedbackDraftSnapshot = {
	category: string;
	subject: string;
	body: string;
	attachment: { objectKey: string; submissionId: string; name: string } | null;
	uploadPending: boolean;
	uploadGeneration: string;
};

function value(root: Root, id: string): string {
	return (byId(root, id) as HTMLInputElement | HTMLTextAreaElement | null)?.value ?? '';
}

function links(root: Root, id: string): LinkDraft[] {
	return Array.from(byId(root, id)?.querySelectorAll<HTMLElement>('.idea-link-row') ?? []).map((row) => ({
		url: row.querySelector<HTMLInputElement>('.idea-link-url')?.value ?? '',
		label: row.querySelector<HTMLInputElement>('.idea-link-desc')?.value ?? '',
	}));
}

function attachments(root: Root, id: string): AttachmentDraft[] {
	return Array.from(byId(root, id)?.querySelectorAll<HTMLElement>('[data-uploaded-key]') ?? []).map((row) => ({
		objectKey: row.dataset.uploadedKey ?? '',
		submissionId: row.dataset.uploadedSubmissionId ?? '',
		name: row.dataset.uploadedName ?? '',
		type: row.dataset.uploadedType ?? '',
		size: row.dataset.uploadedSize ?? '',
	}));
}

function activeStep(root: Root, prefix: 'wizardPanel' | 'aiWizardPanel'): number {
	for (let step = 1; step <= 3; step += 1) {
		if (byId(root, `${prefix}${step}`)?.classList.contains('active')) return step;
	}
	return 1;
}

export function captureIdeaDraft(root: Root): IdeaDraftSnapshot {
	return {
		title: value(root, 'ideaTitle'), subtitle: value(root, 'ideaSubtitle'), strengths: value(root, 'ideaStrengths'), weaknesses: value(root, 'ideaWeaknesses'),
		links: links(root, 'ideaLinkList'), attachments: attachments(root, 'ideaAttachmentList'), step: activeStep(root, 'wizardPanel'),
	};
}

export function captureAiDraft(root: Root): AiDraftSnapshot {
	return {
		title: value(root, 'aiTopicTitle'), subtitle: value(root, 'aiTopicSubtitle'), strengths: value(root, 'aiTopicStrengths'), weaknesses: value(root, 'aiTopicWeaknesses'),
		links: links(root, 'aiLinkList'), attachments: attachments(root, 'aiAttachmentList'), step: activeStep(root, 'aiWizardPanel'),
		chapters: value(root, 'aiTopicChapters'), preset: root.querySelector<HTMLInputElement>('input[name="aiChapterPreset"]:checked')?.value ?? '',
	};
}

export function captureFeedbackDraft(root: Root): FeedbackDraftSnapshot {
	const preview = byId(root, 'feedbackFilePreview');
	const objectKey = preview?.dataset.uploadedKey ?? '';
	return {
		category: root.querySelector<HTMLElement>('[data-cat].active, [data-cat].selected')?.dataset.cat ?? '',
		subject: value(root, 'feedbackSubject'), body: value(root, 'feedbackBody'),
		attachment: objectKey ? { objectKey, submissionId: preview?.dataset.uploadedSubmissionId ?? '', name: byId(root, 'feedbackFileName')?.textContent ?? '' } : null,
		uploadPending: root.querySelector<HTMLElement>('#view-feedback')?.dataset.feedbackUploadPending === 'true',
		uploadGeneration: root.querySelector<HTMLElement>('#view-feedback')?.dataset.feedbackUploadGeneration ?? '',
	};
}

export function sameDraft(left: unknown, right: unknown): boolean {
	return JSON.stringify(left) === JSON.stringify(right);
}

export function beginLongFormSubmission(surface: HTMLElement, _snapshot: unknown): { sequence: number; attempt: string } {
	const sequence = Number.parseInt(surface.dataset.submissionSequence ?? '0', 10) + 1;
	const attempt = crypto.randomUUID();
	surface.dataset.submissionSequence = String(sequence);
	surface.dataset.uiAttempt = attempt;
	surface.dataset.submitting = 'true';
	return { sequence, attempt };
}

export function isLatestLongFormSubmission(surface: HTMLElement | null, sequence: number): boolean {
	return surface?.dataset.submissionSequence === String(sequence);
}

export function ownsLongFormUi(surface: HTMLElement | null, attempt: string): boolean {
	return surface?.dataset.uiAttempt === attempt && surface.classList.contains('active');
}

export function submissionKeyForDraft(surface: HTMLElement, key: 'submissionId' | 'idempotencyKey', fingerprint: unknown): string {
	const serialized = JSON.stringify(fingerprint);
	if (surface.dataset.submissionFingerprint === serialized && surface.dataset[key]) return surface.dataset[key]!;
	const value = crypto.randomUUID();
	surface.dataset[key] = value;
	surface.dataset.submissionFingerprint = serialized;
	return value;
}

export function clearLongFormSubmissionDraft(surface: HTMLElement | null): void {
	if (!surface) return;
	delete surface.dataset.submissionDraft;
	delete surface.dataset.submissionFingerprint;
}
